import React, { useState, useCallback } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  LinearProgress,
  useTheme,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import MeetingBookingForm from "./MeetingBookingForm";
import {
  MeetingFormData,
  getDefaultMeetingFormData,
} from "./types";
import { WorkItemTypeEnum } from "../../store/services/timelineApiRTK";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { config } from "../../lib/config";

interface MeetingBookingDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (meetingData: MeetingFormData) => Promise<void>;
  workItemType?: WorkItemTypeEnum;
  customerNo: number;
  customerName: string;
  isLoading?: boolean;
}

const MeetingBookingDialog: React.FC<MeetingBookingDialogProps> = ({
  open,
  onClose,
  onSubmit,
  workItemType,
  customerName,
  customerNo,
  isLoading = false,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<MeetingFormData>(
    getDefaultMeetingFormData()
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAttendeeWarning, setNoAttendeeWarning] = useState(false);
  // Sätt initial work item type om det anges
  React.useEffect(() => {
    if (workItemType) {
      setFormData((prev) => ({
        ...prev,
        workItemType,
      }));
    }
  }, [workItemType]);

  // Hantera formulärdataändringar
  const handleFormChange = useCallback((newData: MeetingFormData) => {
    setFormData(newData);
    if (newData.attendees.length > 0) {
      setNoAttendeeWarning(false);
    }
  }, []);

  // Hantera stängning av dialogen
  const handleClose = () => {
    if (!isLoading && !submitting) {
      setError(null);
      setNoAttendeeWarning(false);
      onClose();
    }
  };

  // Hantera formulärsändning
  const handleSubmit = async () => {
    // Validera formuläret
    if (!formData.subject.trim()) {
      setError("Mötesämne måste anges");
      return;
    }

    // Utan deltagare krävs en extra bekräftelse innan bokningen går igenom
    if (formData.attendees.length === 0 && !noAttendeeWarning) {
      setError(null);
      setNoAttendeeWarning(true);
      return;
    }

    // Validate location for physical meetings
    if (workItemType === WorkItemTypeEnum.FollowUpPhysicalMeeting && !formData.location?.trim()) {
      setError("Plats måste anges för fysiska möten");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await onSubmit(formData);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ett fel uppstod när mötet skulle bokas"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {/* Framstegsindikator */}
      {(isLoading || submitting) && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
          }}
        />
      )}

      {/* Rubrik */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          bgcolor: "background.default",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <MicrosoftIcon sx={{ mr: 1.5, color: "primary.main" }} />
          <Typography variant="h6">Boka möte med kalenderhändelse</Typography>
        </Box>

        <IconButton
          edge="end"
          onClick={handleClose}
          aria-label="stäng"
          disabled={isLoading || submitting}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {config.isDevelopment && (
          <Box sx={{ mb: 2, p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
            <Typography variant="body2">Du är på test, mötesinbjudningar går ut till kund ifall du använder deras epost. </Typography>
          </Box>
        )}
        {noAttendeeWarning && formData.attendees.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Inga deltagare är tillagda – mötet skapas bara i din egen kalender
            och ingen inbjudan skickas ut. Klicka på "Boka utan deltagare" för
            att fortsätta.
          </Alert>
        )}
        <MeetingBookingForm
          formData={formData}
          onFormChange={handleFormChange}
          error={error}
          customerNo={customerNo}
          customerName={customerName}
          workItemType={workItemType}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading || submitting}
          variant="outlined"
        >
          Avbryt
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting
            ? "Bokar möte..."
            : noAttendeeWarning && formData.attendees.length === 0
              ? "Boka utan deltagare"
              : "Boka möte"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetingBookingDialog;
